/**
 * Organization-related React Query hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { identityServiceClient } from '../client'
import { staticCacheConfig, type QueryOptions } from '../../../lib/utils'
import {
  OrganizationCreate,
  OrganizationResponse,
  OrganizationsQueryParams,
  OrganizationsResponse,
  OrganizationUpdate,
} from '../'

// Query hooks for organizations
export const useOrganizations = (
  params?: OrganizationsQueryParams,
  options?: QueryOptions<OrganizationsResponse, Error>
) => {
  return useQuery({
    queryKey: ['organizations', params],
    queryFn: async () => {
      const response = await identityServiceClient.GET('/organizations', {
        params: { query: params },
      })
      if (response.error) throw response.error
      return response.data
    },
    ...staticCacheConfig,
    ...options,
  })
}

export const useOrganization = (
  organizationId: string,
  options?: QueryOptions<OrganizationResponse, Error, string>
) => {
  return useQuery({
    queryKey: ['organization', organizationId],
    queryFn: async () => {
      const response = await identityServiceClient.GET('/organizations/{id}', {
        params: { path: { id: organizationId } },
      })
      if (response.error) throw response.error
      return response.data
    },
    enabled: !!organizationId,
    ...staticCacheConfig,
    ...options,
  })
}

// Mutation hooks for organizations
export const useCreateOrganization = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (organizationData: OrganizationCreate) => {
      const response = await identityServiceClient.POST('/organizations', {
        body: organizationData,
      })
      if (response.error) throw response.error
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] })
    },
  })
}

export const useUpdateOrganization = (organizationId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (organizationData: OrganizationUpdate) => {
      const response = await identityServiceClient.PUT('/organizations/{id}', {
        params: { path: { id: organizationId } },
        body: organizationData,
      })
      if (response.error) throw response.error
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] })
      queryClient.invalidateQueries({ queryKey: ['organization', organizationId] })
    },
  })
}

export const useDeleteOrganization = (organizationId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response = await identityServiceClient.DELETE('/organizations/{id}', {
        params: { path: { id: organizationId } },
      })
      if (response.error) throw response.error
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] })
      queryClient.removeQueries({ queryKey: ['organization', organizationId] })
    },
  })
}
